import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { StatusDot } from '@/components/ui/Badge';
import { AuditLogFeed, type AuditLogEntry } from '@/components/ui/AuditLogFeed';
import { CommentsTab } from '@/components/comments';
import { DependenciesTab } from '@/components/dependencies/DependenciesTab';
import { ResourcesTab } from '@/components/resources/ResourcesTab';
import { initiativeApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/stores/appStore';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { ICON_MAP } from '@/components/modals/IconPicker';
import { InitiativeModal } from '@/components/modals';
import { formatActualDate, formatFiscalQuarterHint } from '@/utils/dateRange';

type TabId = 'main' | 'dependencies' | 'comments' | 'audit' | 'resources';

export function InitiativeFullPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { quarters } = useAppStore();
  const labs = useLabsFeatures();
  const fc = useFieldConfig();

  const [initiative, setInitiative] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('main');
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const canEdit = user?.role === 'ADMIN' || user?.role === 'EDITOR';

  const handleToggleStar = async () => {
    if (!initiative) return;
    const newValue = !initiative.isStarred;
    setInitiative((prev: any) => ({ ...prev, isStarred: newValue })); // optimistic
    try {
      await initiativeApi.update(initiative.id, { isStarred: newValue });
    } catch {
      setInitiative((prev: any) => ({ ...prev, isStarred: !newValue })); // revert on error
    }
  };

  const loadInitiative = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await initiativeApi.getById(id);
      if (!data) {
        setNotFound(true);
      } else {
        setInitiative(data);
        setNotFound(false);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInitiative();
  }, [loadInitiative]);

  // Fetch audit log when switching to audit tab
  useEffect(() => {
    if (activeTab === 'audit' && id) {
      setAuditLoading(true);
      initiativeApi
        .getAuditLog(id)
        .then((data) => setAuditLogs(data))
        .catch((err) => console.error('Failed to load audit log:', err))
        .finally(() => setAuditLoading(false));
    }
  }, [activeTab, id]);

  const getQuarterLabel = (key: string) => {
    const quarter = quarters.find((q) => q.key === key);
    return quarter?.fullLabel || key;
  };

  // Tab definitions
  const TABS: { id: TabId; label: string | React.ReactNode }[] = [
    { id: 'main', label: 'Main' },
    ...(labs.isEnabled('dependencies')
      ? [{ id: 'dependencies' as TabId, label: 'Dependencies' }]
      : []),
    ...(labs.isEnabled('comments')
      ? [{ id: 'comments' as TabId, label: 'Comments' }]
      : []),
    ...(labs.isEnabled('auditLog')
      ? [{ id: 'audit' as TabId, label: 'Audit Log' }]
      : []),
    ...(labs.isEnabled('resources')
      ? [{ id: 'resources' as TabId, label: 'Resources' }]
      : []),
  ];

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <Navbar />
        <main className="flex-1 overflow-auto flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </main>
      </div>
    );
  }

  if (notFound || !initiative) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <Navbar />
        <main className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-6 py-6">
            <button
              onClick={() => navigate('/list')}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to List
            </button>
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Initiative not found.</p>
              <p className="text-gray-400 text-sm mt-1">
                It may have been deleted or the link is incorrect.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const classification = initiative.classification;
  const status = initiative.status;
  const owner = initiative.user;

  // Resolve product and sub-product names from the included relation
  const subProduct = initiative.subProduct;
  const productName = subProduct?.product?.name || '';
  const subProductName = subProduct?.name || '';

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto px-6 py-6">
          {/* Back button */}
          <button
            onClick={() => navigate('/list')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to List
          </button>

          {/* Initiative header */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-start gap-3 min-w-0">
              {classification && (
                <div
                  className="w-1 self-stretch rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: classification.color }}
                />
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 leading-snug">
                  {initiative.title}
                </h1>
                {classification && (
                  <span
                    className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded"
                    style={{
                      backgroundColor: `${classification.color}20`,
                      color: classification.color,
                    }}
                  >
                    {classification.name}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Star toggle — always visible, anyone can star */}
              <button
                onClick={handleToggleStar}
                className={`p-1.5 rounded-md transition-colors ${
                  initiative.isStarred
                    ? 'text-amber-400 hover:text-amber-500'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title={initiative.isStarred ? 'Remove star' : 'Star this initiative'}
              >
                <svg className="w-5 h-5" fill={initiative.isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.95-.69l1.519-4.674z" />
                </svg>
              </button>
              {canEdit && (
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {/* Tab bar */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-6" aria-label="Initiative tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div>
            {/* Main Tab */}
            {activeTab === 'main' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                {/* Fields grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Status
                    </label>
                    {status ? (
                      <div className="flex items-center gap-2">
                        <StatusDot color={status.color} />
                        <span className="text-sm text-gray-900">{status.name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </div>

                  {/* Owner */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {fc.label('owner')}
                    </label>
                    <p className="text-sm text-gray-900">{owner?.name || '—'}</p>
                  </div>

                  {/* Start period */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {fc.label('start')}
                    </label>
                    {initiative.datePrecision === 'day' ? (
                      <>
                        <p className="text-sm text-gray-900">{formatActualDate(initiative.startDate)}</p>
                        <p className="text-xs text-gray-400">{formatFiscalQuarterHint(initiative.startDate, quarters)}</p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-900">
                        {initiative.startQuarter
                          ? getQuarterLabel(initiative.startQuarter)
                          : '—'}
                      </p>
                    )}
                  </div>

                  {/* End period */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {fc.label('end')}
                    </label>
                    {initiative.datePrecision === 'day' ? (
                      initiative.endDate ? (
                        <>
                          <p className="text-sm text-gray-900">{formatActualDate(initiative.endDate)}</p>
                          <p className="text-xs text-gray-400">{formatFiscalQuarterHint(initiative.endDate, quarters)}</p>
                        </>
                      ) : (
                        <p className="text-sm text-gray-900">—</p>
                      )
                    ) : (
                      <p className="text-sm text-gray-900">
                        {initiative.endQuarter
                          ? getQuarterLabel(initiative.endQuarter)
                          : '—'}
                      </p>
                    )}
                  </div>

                  {/* Product */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {fc.label('product')}
                    </label>
                    <p className="text-sm text-gray-900">{productName || '—'}</p>
                  </div>

                  {/* Sub-product */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {fc.label('subProduct')}
                    </label>
                    <p className="text-sm text-gray-900">{subProductName || '—'}</p>
                  </div>

                  {/* Classification */}
                  {fc.isVisible('classification') && classification && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        {fc.label('classification')}
                      </label>
                      <span
                        className="inline-block px-2 py-0.5 text-xs font-medium rounded"
                        style={{
                          backgroundColor: `${classification.color}20`,
                          color: classification.color,
                        }}
                      >
                        {classification.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {initiative.tags && initiative.tags.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      {fc.label('tags')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {initiative.tags.map((initTag: any) => {
                        const IconComponent = initTag.tag?.icon
                          ? ICON_MAP[initTag.tag.icon]
                          : null;
                        return (
                          <div
                            key={initTag.tagId}
                            className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md text-sm text-gray-600"
                          >
                            {IconComponent && (
                              <IconComponent className="w-4 h-4" />
                            )}
                            <span>{initTag.tag?.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {initiative.notes && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Notes
                    </label>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {initiative.notes}
                    </p>
                  </div>
                )}

                {/* Description */}
                {initiative.description && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Description
                    </label>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {initiative.description}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Dependencies Tab */}
            {activeTab === 'dependencies' && id && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <DependenciesTab initiativeId={id} />
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && id && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <CommentsTab initiativeId={id} />
              </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <AuditLogFeed entries={auditLogs} loading={auditLoading} />
              </div>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && id && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <ResourcesTab initiativeId={id} />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit modal */}
      {editModalOpen && (
        <InitiativeModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          initiative={initiative}
          onSuccess={() => {
            setEditModalOpen(false);
            loadInitiative();
          }}
        />
      )}
    </div>
  );
}
